'use client'

import React, { useEffect, useMemo, useState } from 'react'
import Image from 'next/image'
import { createPortal } from 'react-dom'
import { Currency, ITheme, Language } from '@/lib/interfaces/interfaces'
import { formatMoney } from '@/lib/utils/format'
import { NextFont } from 'next/dist/compiled/@next/font'
import { allergensList, getAllergenName } from '@/lib/utils/allergensPath'
import DetailedPopUp from './DetailedPopUp'

interface Props {
  name: string
  description?: string
  price?: number
  halfprice?: number
  tapaprice?: number
  priceLabel?: string
  halfpriceLabel?: string
  tapapriceLabel?: string
  allergens?: string[]
  imageSource?: string
  theme: ITheme
  fontStyle: {
    fontFamily: NextFont
    fontSize: string
  }
  currency: Currency
  language: Language
  orderingActive: boolean
  priceMarkup?: number
  itemId: string
}

const ModernItem = ({
  name,
  description,
  price,
  halfprice,
  tapaprice,
  priceLabel,
  halfpriceLabel,
  tapapriceLabel,
  allergens,
  imageSource,
  theme,
  fontStyle,
  currency,
  language,
  orderingActive,
  priceMarkup = 0,
  itemId
}: Props) => {
  const [showFullItem, setShowFullItem] = useState(false)
  const [showLightbox, setShowLightbox] = useState(false)
  const [lightboxNatural, setLightboxNatural] = useState<{ w: number; h: number } | null>(null)
  const [lightboxViewport, setLightboxViewport] = useState<{ w: number; h: number } | null>(null)

  const lightboxSize = useMemo(() => {
    if (!lightboxNatural || !lightboxViewport) return null
    const maxW = Math.min(1100, lightboxViewport.w - 48)
    const maxH = lightboxViewport.h * 0.9
    const scale = Math.min(maxW / lightboxNatural.w, maxH / lightboxNatural.h)
    return {
      w: Math.round(lightboxNatural.w * scale),
      h: Math.round(lightboxNatural.h * scale)
    }
  }, [lightboxNatural, lightboxViewport])

  useEffect(() => {
    if (!showLightbox) return
    const updateViewport = () => {
      setLightboxViewport({ w: window.innerWidth, h: window.innerHeight })
    }
    updateViewport()
    window.addEventListener('resize', updateViewport)
    return () => {
      window.removeEventListener('resize', updateViewport)
    }
  }, [showLightbox])

  useEffect(() => {
    const handlePopState = (event: PopStateEvent) => {
      if (showFullItem) {
        setShowFullItem(false)
      }
      if (showLightbox) {
        setShowLightbox(false)
      }
    }

    window.addEventListener('popstate', handlePopState)
    return () => {
      window.removeEventListener('popstate', handlePopState)
    }
  }, [showFullItem, showLightbox])

  const openPopup = () => {
    // En modo ordering se abre el popup con el widget de pedido
    if (orderingActive) {
      setShowFullItem(true)
      window.history.pushState({ popup: true }, '')
      return
    }

    // Sin ordering, mostrar la imagen en grande si el item tiene foto
    if (!imageSource) return
    setShowLightbox(true)
    window.history.pushState({ lightbox: true }, '')
  }

  const closePopup = () => {
    setShowFullItem(false)
    window.history.back()
  }

  const closeLightbox = () => {
    setShowLightbox(false)
    window.history.back()
  }

  const applyMarkup = (price: number) =>
    // Aplicar markup como cantidad fija solo cuando está en modo ordering (delivery) y el markup es mayor a 0
    orderingActive && priceMarkup > 0 ? price + priceMarkup : price

  return (
    <>
      <div
        className={`modern-item ${imageSource ? 'with-image' : 'list-style'} ${!orderingActive ? 'no-ordering' : ''}`}
        onClick={openPopup}
      >
        {imageSource
          ? (
            // Card style para items con imagen
            <>
              <div className='modern-item-image'>
                <Image
                  src={imageSource}
                  alt={name}
                  fill
                  className='modern-item-img'
                />
              </div>
              <div className='modern-item-content'>
                <div className='modern-item-header'>
                  <h3 className={`modern-item-name ${fontStyle.fontFamily.className}`} style={{ color: theme.items.nameColor, fontSize: `calc(${fontStyle.fontSize} * 1.15)` }}>
                    {name}
                  </h3>
                  <div className='modern-item-price'>
                    {tapaprice !== 0 && tapaprice !== undefined && (
                      <span className={`price-tag ${fontStyle.fontFamily.className}`} style={{ backgroundColor: theme.items.bgPriceColor, color: theme.items.priceTextColor, fontSize: fontStyle.fontSize }} data-price-label={tapapriceLabel?.trim() ? tapapriceLabel.trim() : undefined}>
                        {formatMoney(applyMarkup(tapaprice), currency)}
                      </span>
                    )}
                    {halfprice !== 0 && halfprice !== undefined && (
                      <span className={`price-tag ${fontStyle.fontFamily.className}`} style={{ backgroundColor: theme.items.bgPriceColor, color: theme.items.priceTextColor, fontSize: fontStyle.fontSize }} data-price-label={halfpriceLabel?.trim() ? halfpriceLabel.trim() : undefined}>
                        {formatMoney(applyMarkup(halfprice), currency)}
                      </span>
                    )}
                    {price !== 0 && price !== undefined && (
                      <span className={`price-tag ${fontStyle.fontFamily.className}`} style={{ backgroundColor: theme.items.bgPriceColor, color: theme.items.priceTextColor, fontSize: fontStyle.fontSize }} data-price-label={priceLabel?.trim() ? priceLabel.trim() : undefined}>
                        {formatMoney(applyMarkup(price), currency)}
                      </span>
                    )}
                  </div>
                </div>
                {description && (
                  <p className={`modern-item-description ${fontStyle.fontFamily.className}`} style={{ color: theme.items.descriptionColor, fontSize: fontStyle.fontSize }}>
                    {description}
                  </p>
                )}
                {allergens && allergens.length > 0 && (
                  <div className='modern-item-allergens'>
                    {allergens.map((allergen) => (
                      <div
                        key={allergen}
                        data-allergen={getAllergenName(allergen, language)}
                        className='allergen-icon'
                      >
                        <Image
                          src={allergensList[allergen].img}
                          alt={allergen}
                          width={24}
                          height={24}
                        />
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </>
            )
          : (
            // List style para items sin imagen
            <div className='modern-item-list'>
              <div className='modern-item-list-header'>
                <h3 className={`modern-item-name ${fontStyle.fontFamily.className}`} style={{ color: theme.items.nameColor, fontSize: `calc(${fontStyle.fontSize} * 1.05)` }}>
                  {name}
                </h3>
                <div className='modern-item-price'>
                  {tapaprice !== 0 && tapaprice !== undefined && (
                    <span className={`price-tag ${fontStyle.fontFamily.className}`} style={{ backgroundColor: theme.items.bgPriceColor, color: theme.items.priceTextColor, fontSize: fontStyle.fontSize }} data-price-label={tapapriceLabel?.trim() ? tapapriceLabel.trim() : undefined}>
                      {formatMoney(applyMarkup(tapaprice), currency)}
                    </span>
                  )}
                  {halfprice !== 0 && halfprice !== undefined && (
                    <span className={`price-tag ${fontStyle.fontFamily.className}`} style={{ backgroundColor: theme.items.bgPriceColor, color: theme.items.priceTextColor, fontSize: fontStyle.fontSize }} data-price-label={halfpriceLabel?.trim() ? halfpriceLabel.trim() : undefined}>
                      {formatMoney(applyMarkup(halfprice), currency)}
                    </span>
                  )}
                  {price !== 0 && price !== undefined && (
                    <span className={`price-tag ${fontStyle.fontFamily.className}`} style={{ backgroundColor: theme.items.bgPriceColor, color: theme.items.priceTextColor, fontSize: fontStyle.fontSize }} data-price-label={priceLabel?.trim() ? priceLabel.trim() : undefined}>
                      {formatMoney(applyMarkup(price), currency)}
                    </span>
                  )}
                </div>
              </div>
              {description && (
                <p className={`modern-item-description ${fontStyle.fontFamily.className}`} style={{ color: theme.items.descriptionColor, fontSize: fontStyle.fontSize }}>
                  {description}
                </p>
              )}
              {allergens && allergens.length > 0 && (
                <div className='modern-item-allergens'>
                  {allergens.map((allergen) => (
                    <div
                      key={allergen}
                      data-allergen={getAllergenName(allergen, language)}
                      className='allergen-icon'
                    >
                      <Image
                        src={allergensList[allergen].img}
                        alt={allergen}
                        width={22}
                        height={22}
                      />
                    </div>
                  ))}
                </div>
              )}
            </div>
            )}
      </div>

      {showFullItem && (
        <DetailedPopUp
          name={name}
          description={description}
          allergens={allergens}
          allergenImages={allergens}
          price={price}
          halfprice={halfprice}
          tapaprice={tapaprice}
          priceLabel={priceLabel}
          halfpriceLabel={halfpriceLabel}
          tapapriceLabel={tapapriceLabel}
          setShowFullItem={closePopup}
          imageSource={imageSource}
          theme={theme}
          fontStyle={fontStyle}
          currency={currency}
          language={language}
          orderingActive={orderingActive}
          priceMarkup={priceMarkup}
          itemId={itemId}
        />
      )}

      {showLightbox && imageSource && (
        createPortal(
          <div className='modern-lightbox' onClick={closeLightbox}>
            <div className='modern-lightbox__frame'>
              <button
                className='modern-lightbox__close'
                aria-label='Close'
                onClick={(e) => {
                  e.stopPropagation()
                  closeLightbox()
                }}
              >
                ×
              </button>
              <Image
                src={imageSource}
                alt={name}
                width={1200}
                height={900}
                onLoad={(e) => {
                  const img = e.currentTarget
                  if (img.naturalWidth > 0 && img.naturalHeight > 0) {
                    setLightboxNatural({ w: img.naturalWidth, h: img.naturalHeight })
                  }
                }}
                style={
                  lightboxSize
                    ? { width: lightboxSize.w, height: lightboxSize.h }
                    : undefined
                }
                className='modern-lightbox__img'
              />
            </div>
          </div>,
          document.body
        )
      )}
    </>
  )
}

export default ModernItem
